import { CurrencyPipe } from '@angular/common';
import * as _ from 'lodash';
import { IIbanObject } from '../modules/athora/athora.constants';
import { EPipeKeys } from '../modules/policy/pages/policy-detail/policy-detail.constants';
import { IFormKey } from '../modules/shared/models/forms';
import { IObjectKey } from '../modules/shared/models/objects';
import { IDashboardData, IDashboardOperation, IKpiObject, IOperationKPI } from '../modules/shared/models/operation.model';
import { ATHORA_COMPANY_IDS, DATE_FORMAT_SLASH, EPolicyCompany, EPolicyStatus, IOperationFilter, ISV_COMPANY_IDS } from '../modules/shared/models/policy.model';
import { cannot_cancel_operations, EDashboardStatus, EOperationBadge, EOperationStatus } from '../pages/dashboard/dashboard.constants';
import { LoggerService } from '../services/logger.service';
import moment = require('moment');

export class CommonUtils {

    static isVoid = function (input) {
        return input === 'undefined' || input === undefined || input === null || input === 'null' || input === '' || input === '';
    };

    static isUndefined = function (input) {
        return input === undefined;
    };

    static isVoidObj(obj: any): boolean {
        let control = true;
        if (!CommonUtils.isVoid(obj)) {
            control = Object.keys(obj).reduce((res, k) => res && !(!!obj[k] || obj[k] === false || !isNaN(parseInt(obj[k]))), true);
        }
        return control;
    }

    static assign(object: any): any {
        return _.cloneDeep(object);
    }

    static checkAndGet(obj: any, path: string, defaultValue: any = null, type?: 'string' | 'number'): any {
        if (path != null) {
            const obj2Return = _.get(obj, path, defaultValue);
            if (!CommonUtils.isVoid(obj2Return)) {
                switch (type) {
                    case 'number':
                        return +obj2Return;
                    case 'string':
                        return obj2Return.toString();
                    default:
                        return obj2Return;
                }
            } else {
                return obj2Return;
            }
        } else {
            return defaultValue;
        }
    }

    static arrayInclude(array: any[], a: any, compareFunction = null): boolean {
        if (typeof a === 'object') {
            let index = -1;
            if (!!compareFunction) {
                index = array.findIndex((b) => compareFunction(a, b));
            } else {
                index = array.findIndex((b) => this.getObjectDiff(a, b).length === 0);
            }
            return index >= 0;
        }
        return array.indexOf(a) >= 0;
    }

    private static getObjectDiff(obj1, obj2) {
        const diff = Object.keys(obj1).reduce((result, key) => {
            if (!obj2.hasOwnProperty(key)) {
                result.push(key);
            } else if (_.isEqual(obj1[key], obj2[key])) {
                const resultKeyIndex = result.indexOf(key);
                result.splice(resultKeyIndex, 1);
            }
            return result;
        }, Object.keys(obj2));
        return diff;
    }

    static round(value: number, decimals: number = 0): number {
        const roundFactor = Math.pow(10, decimals);
        return Math.round(value * roundFactor) / roundFactor;
    }

    /** Genera una stringa random con un dato pattern */
    static generateRandomId(length = 16, pattern = '[a-zA-Z0-9]'): string {
        const password = Array.apply(null, { 'length': length })
            .map(() => {
                let result;
                while (true) {
                    result = String.fromCharCode(this.getRandomByte());
                    const regexp = new RegExp(pattern);
                    if (regexp.test(result)) {
                        return result;
                    }
                }
            })
            .join('');
        return password;
    }

    private static getRandomByte(): number {
        if (window.crypto && window.crypto.getRandomValues) {
            var result = new Uint8Array(1);
            window.crypto.getRandomValues(result);
            return result[0];
        }
    }

    static getPlaceholders(keysLength: number, columns: number): number[] {
        const rest = keysLength % columns;
        const items = rest ? columns - rest : 0;
        return new Array(items);
    }

    static getValueByPath(obj: any, key: IObjectKey): any {
        let value = key?.key != null ? key?.key.split('.').reduce((acc, part) => acc && acc[part], obj) : null;
        value = value !== null && value !== undefined && key?.key !== null ? value : 'N/A';
        if (key?.pipe?.key === EPipeKeys.CURRENCY) {
            const currencyPipe = new CurrencyPipe('it-IT');
            if (!isNaN(value)) {
                return currencyPipe.transform(value, key?.pipe?.value);
            } else {
              const val = value.replace(',', '.');
              if (!isNaN(val)) {
                return currencyPipe.transform(val, key?.pipe?.value);
              }
            }
        } else if (key?.pipe?.key === EPipeKeys.PERCENTAGE && !isNaN(value)) {
            return `${value} ${key?.pipe?.value}`;
        } else if (key?.pipe?.key === EPipeKeys.DATE_FROM_BE && value && moment(value).isValid()) {
            return moment(value).format(DATE_FORMAT_SLASH);
        }
        return value;
    }

    static getCompanyImage(companyId: string) {
        if (companyId && companyId.toLowerCase() === EPolicyCompany.ATHORA) {
            return 'assets/images/athora_logo_full.png';
        } else if (companyId && companyId.toLowerCase() === EPolicyCompany.ISLD) {
            return 'assets/images/isl_logo_full.png';
        } else {
            return 'assets/images/isv_logo_full.png';
        }
    }

    static getCompanyCode(companyId: string): 'ath' | 'isv' {
        if (companyId) {
            if (CommonUtils.arrayInclude(ATHORA_COMPANY_IDS, companyId.toLowerCase())) {
                return 'ath';
            } else if (CommonUtils.arrayInclude(ISV_COMPANY_IDS, companyId.toLowerCase())) {
                return 'isv';
            }
        }
        return null;
    }

    static getOperationTitle(operationId: string, operationsNamesList: IOperationFilter[]): string {
        const operation: IOperationFilter = operationsNamesList?.find((operation: IOperationFilter) => operation?.operationCode === operationId);
        if (!operation) {
          switch (operationId) {
            case '_ARISP':
              return 'Annullo riscatto parziale';
            case '_ARIST':
              return 'Annullo riscatto totale';
            case '_ANAPP':
              return 'Annullo appendice';
          }
        }
        return CommonUtils.checkAndGet(operation, 'operationDesc', null);
    }

    static getStatusBadgeStyle(policyStatus: string): string {
        let statusBadge: string;
        switch (policyStatus) {
            case EPolicyStatus.ATTIVA:
                statusBadge = "badge-green";
                break;
            case EPolicyStatus.ESTINTA:
                statusBadge = "badge-red";
                break;
            case EPolicyStatus.TRASFERITA:
            default:
                statusBadge = "badge-light-blue";
                break;
        }
        return statusBadge;
    }

    static populateIbanOptions(keys: IFormKey[], ibanList: IIbanObject[], standardFormat: boolean = false): IFormKey[] {
      if (keys && ibanList) {
        const options = standardFormat ?
            ibanList.map(ibanObject =>
                ({
                    key: this.formatIBANString(ibanObject),
                    value: this.formatIBANString(ibanObject)
                })) : ibanList.map(ibanObject => ({
                    key: CommonUtils.checkAndGet(ibanObject, 'abiRapporto', null) + CommonUtils.checkAndGet(ibanObject, 'cabRapporto', null) + CommonUtils.checkAndGet(ibanObject, 'numeroRapporto', null),
                    value: CommonUtils.checkAndGet(ibanObject, 'abiRapporto', null) + CommonUtils.checkAndGet(ibanObject, 'cabRapporto', null) + CommonUtils.checkAndGet(ibanObject, 'numeroRapporto', null)
                }));
        LoggerService.logInfo(this.constructor.name, 'populateIbanOptions', options);
        keys.forEach(key => {
          if (key.controlName === 'iban') {
            key.options = options;
          }
        })
          return keys;
        }
    }

    static manageGeneralKpis(object: IKpiObject): IOperationKPI[] {
        const operationsKpis: IOperationKPI[] = [];
        if (object) {
            Object.keys(object).forEach(key => {
                operationsKpis.push({
                    label: key,
                    key: object[key]
                })
            });
        }
        return operationsKpis;
    }

    static manageDashboardData(data: IDashboardData[]): IDashboardOperation[] {
        let dashboardOperations: IDashboardOperation[] = [];
        if (data) {
          dashboardOperations = data.map((element: IDashboardData) => ({
            id: element?.id,
            movementId: element?.movementId,
            operationDate: element?.operationDate,
            companyPolicyNumber: element?.companyPolicyNumber,
            bperPolicyNumber: element?.bperPolicyNumber,
            bperContractNumber: element?.bperContractNumber,
            operationId: element?.operationTypeCode,
            operationDescription: element?.operationTypeDescription,
            userId: element?.operatorId,
            userAbi: element?.userAbi,
            userAgencyCode: element?.userAgencyCode,
            companyId: element?.companyCode?.toLowerCase(),
            status: element?.operationStatus,
            benefitAtDate: element?.operationAmount?.toString(),
            showIcon: !cannot_cancel_operations.includes(element?.operationStatus as EDashboardStatus),
            completionDate: element?.completionDate
          }));
        }
        return dashboardOperations;
    }

    static getStatusLabel(item: IDashboardOperation): string {
        const operationStatus = CommonUtils.checkAndGet(item, 'status', null);
        const statusLabel = EOperationStatus[operationStatus];
        return statusLabel ? statusLabel : operationStatus;
    }

    static getStatusBadge(item: IDashboardOperation): string {
        const operationStatus = CommonUtils.checkAndGet(item, 'status', null);
        const badgeStyle = EOperationBadge[operationStatus];
        return badgeStyle ? badgeStyle : (
            operationStatus ? 'badge-yellow' : null
        );
    }

    static formatIBANString(ibanObj: IIbanObject): string {
        if (ibanObj) {
            const codicePaese: string = CommonUtils.checkAndGet(ibanObj, 'codicePaese', null);
            const codEur: string = CommonUtils.checkAndGet(ibanObj, 'codiceInternazionaleEuropeo', null);
            const codNazionale: string = CommonUtils.checkAndGet(ibanObj, 'codiceNazionaleBBAN', null);
            const abi: string = CommonUtils.checkAndGet(ibanObj, 'abiRapporto', null);
            const cab: string = CommonUtils.checkAndGet(ibanObj, 'cabRapporto', null);
            const numeroConto: string = CommonUtils.checkAndGet(ibanObj, 'numeroRapporto', null);
            return `${codicePaese}${codEur}${codNazionale}${abi}${cab}${numeroConto}`;
        }
        return null;
    }

}
